import { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Chip, RichTextEditor, Select } from "@legal-500/legal500-design-system";
import PermissionGate from "@/components/Permissions/PermissionGate";
import { usePermissions } from "@/hooks/usePermissions";
import {
  copyTemplates as copyTemplatesApi,
  type CopyTemplate,
  type EntityScope,
  type Language,
  type Placeholder,
  type RankingBand,
  type TemplateType,
} from "@/api/copyTemplates.service";
import { LANGUAGES, entityScopeLabel, rankingBandLabel, templateTypeLabel } from "./copy-types";
import styles from "./CopyTemplates.module.css";

type TemplateEditorPanelProps = {
  templateType: TemplateType;
  entityScope: EntityScope;
  rankingBand: RankingBand;
  language: Language;
  onLanguageChange: (language: Language) => void;
  /** The saved rows for this slot, keyed by language. */
  savedByLanguage: Partial<Record<Language, CopyTemplate>>;
  placeholders: Placeholder[];
  onSaved: (message: string) => void;
  onError: (message: string) => void;
  onClose: () => void;
  refetchTemplates: () => void;
};

type ApiError = Error & { status?: number; detailsRaw?: unknown };

function unapprovedNamesFrom(error: ApiError): string[] {
  const details = error.detailsRaw as { unapprovedPlaceholders?: unknown } | undefined;
  const names = details?.unapprovedPlaceholders;
  return Array.isArray(names) ? names.map(String) : [];
}

function saveErrorMessage(error: unknown): string {
  const apiError = error as ApiError;
  if (apiError?.status === 400) {
    const names = unapprovedNamesFrom(apiError);
    if (names.length > 0) {
      return `These fields are not on the approved list: ${names.join(", ")}. Remove them and save again.`;
    }
  }
  if (apiError?.status === 409) {
    return "Someone else saved this template while it was open. Reopen it to see their text, then apply your change.";
  }
  return apiError?.message || "The template could not be saved.";
}

export function TemplateEditorPanel({
  templateType,
  entityScope,
  rankingBand,
  language,
  onLanguageChange,
  savedByLanguage,
  placeholders,
  onSaved,
  onError,
  onClose,
  refetchTemplates,
}: TemplateEditorPanelProps) {
  const { hasPermission } = usePermissions();
  const canWrite = hasPermission("staff.marketing.write");
  const saved = savedByLanguage[language];
  const [content, setContent] = useState(saved?.content ?? "");
  const [previewHtml, setPreviewHtml] = useState("");
  const [saving, setSaving] = useState(false);
  const [insertName, setInsertName] = useState("");

  useEffect(() => {
    setContent(saved?.content ?? "");
  }, [saved?.id, saved?.version, language, templateType, entityScope, rankingBand]);

  useEffect(() => {
    if (!content) {
      setPreviewHtml("");
      return;
    }

    let cancelled = false;
    const timer = setTimeout(() => {
      copyTemplatesApi
        .preview({ entityScope, language, rankingBand, content })
        .then(result => {
          if (!cancelled) setPreviewHtml(result.html);
        })
        .catch(() => {
          if (!cancelled) setPreviewHtml("<p>The preview could not be rendered. Try again.</p>");
        });
    }, 400);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [content, entityScope, language, rankingBand]);

  const placeholderOptions = useMemo(
    () => [
      { value: "", label: "Insert field…" },
      ...placeholders.map(item => ({ value: item.name, label: item.label })),
    ],
    [placeholders],
  );

  const insertPlaceholder = useCallback((name: string) => {
    if (!name) return;
    setContent(current => `${current} {{${name}}} `.trimStart());
    setInsertName("");
  }, []);

  async function handleSave() {
    setSaving(true);
    try {
      await copyTemplatesApi.save({
        templateType,
        entityScope,
        rankingBand,
        language,
        content,
        ...(saved ? { expectedVersion: saved.version } : {}),
      });
      onSaved(`${templateTypeLabel(templateType)} saved.`);
      refetchTemplates();
    } catch (error) {
      onError(saveErrorMessage(error));
    } finally {
      setSaving(false);
    }
  }

  return (
    <section className={styles.editor}>
      <header className={styles.editorHeader}>
        <div>
          <h2 className={styles.editorTitle}>{templateTypeLabel(templateType)}</h2>
          <p className={styles.savedNote}>
            {entityScopeLabel(entityScope)} · {rankingBandLabel(rankingBand)}
            {saved
              ? ` · version ${saved.version}, saved ${new Date(saved.updatedAt).toLocaleString()}${
                  saved.updatedBy ? ` by ${saved.updatedBy}` : ""
                }`
              : " · not written yet"}
          </p>
        </div>
        <Button variant="outlined" color="default" size="small" onClick={onClose}>
          Close
        </Button>
      </header>

      <div className={styles.languageTabs}>
        {LANGUAGES.map(option => (
          <Chip
            key={option.value}
            color={option.value === language ? "primary" : "default"}
            variant={option.value === language ? "filled" : "outlined"}
            onClick={() => onLanguageChange(option.value)}
          >
            {savedByLanguage[option.value] ? option.label : `${option.label} — empty`}
          </Chip>
        ))}
      </div>

      <div className={styles.editorGrid}>
        <div className={styles.editorColumn}>
          <span className={styles.columnLabel}>Text</span>
          <div className={styles.insertRow}>
            <div className={styles.insertField}>
              <Select
                label=""
                value={insertName}
                options={placeholderOptions}
                onChange={event => insertPlaceholder(event.target.value)}
              />
            </div>
          </div>
          <RichTextEditor
            value={content}
            onChange={setContent}
            preset="basic_enhanced"
            minHeight={280}
            label=""
            disabled={!canWrite}
          />
        </div>

        <div className={styles.editorColumn}>
          <span className={styles.columnLabel}>Preview</span>
          <p className={styles.previewNote}>Rendered with the sample ranking.</p>
          <div
            className={styles.preview}
            // The preview comes from marketing-api, which cleans the content
            // against the editor allow-list before filling the fields.
            dangerouslySetInnerHTML={{ __html: previewHtml }}
          />
        </div>
      </div>

      <PermissionGate permission="staff.marketing.write">
        <div className={styles.actions}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSave}
            disabled={saving || content.length === 0}
          >
            {saving ? "Saving…" : "Save"}
          </Button>
        </div>
      </PermissionGate>
    </section>
  );
}
